// DiscoveryPanel — master-flow inline panel inside the jetson card.
//
// Per design doc §2.2 (the master autodiscovery happy-path narrative)
// and §3 (component responsibilities). This is the central master-flow
// UI: timeline + controls + credential prompts + summary.
//
// State machine:
//   no run yet                → "Start autodiscovery" button
//   created/running           → timeline + Cancel + Collapse
//   paused_on_creds           → timeline + CredentialPrompt + Cancel
//   ok/partial/failed/cancelled → summary + "Run again" + "Done"
//
// Navigation persistence: the runId lives in URL hash (`#discovery=<id>`).
// Collapsing the panel sets a local state that pauses the polling hook
// but keeps the hash. Coming back (or refreshing) re-binds to the same
// run.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Play,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import {
  createDiscoveryRun,
  isLive,
  isTerminal,
  type DiscoveryRunStatus,
} from '@/api/discoveryRuns';
import { useDiscoveryRun } from '@/hooks/useDiscoveryRun';
import type { DiscoveryEvent, TargetSpec } from '@/api/discovery';
import { DiscoveryTimeline } from './DiscoveryTimeline';
import { CredentialPrompt } from './CredentialPrompt';

export interface DiscoveryPanelProps {
  /** LocalComputer UUID — used in POST /discovery-runs/. */
  lcId: string;
  /** Zone ID — used in POST /discovery-runs/. */
  zoneId: number | string;
}

const HASH_KEY = 'discovery';

export function DiscoveryPanel({ lcId, zoneId }: DiscoveryPanelProps) {
  const { t } = useT();

  // ---- runId state — URL hash is source of truth -------------------
  const [runId, setRunId] = useState<string | null>(() => readHash());
  const [collapsed, setCollapsed] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submittingCreds, setSubmittingCreds] = useState(false);

  // Keep local state in sync with URL hash changes (e.g. browser back).
  useEffect(() => {
    const onHashChange = () => setRunId(readHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // ---- polling hook ------------------------------------------------
  const { run, events, status, isLoading, refresh, cancel, submitCredentials, skipDevice } =
    useDiscoveryRun(runId, { paused: collapsed });

  // ---- credentials-needed detection --------------------------------
  const pendingCredsEvent = useMemo(
    () => findPendingCredentialNeeded(events),
    [events],
  );

  // ---- actions -----------------------------------------------------
  const startRun = useCallback(async () => {
    if (starting) return;
    setStarting(true);
    try {
      const target: TargetSpec = { kind: 'subnet-scan', vendor_hint: 'hikvision' };
      const r = await createDiscoveryRun({
        local_computer: lcId,
        zone: typeof zoneId === 'string' ? parseInt(zoneId, 10) : zoneId,
        kind: 'master',
        target_spec: target,
      });
      writeHash(r.data.id);
      setRunId(r.data.id);
      setCollapsed(false);
    } catch (e) {
      console.error('Failed to start discovery run', e);
    } finally {
      setStarting(false);
    }
  }, [starting, lcId, zoneId]);

  const onSubmitCreds = useCallback(
    async (candidates: Parameters<typeof submitCredentials>[0]) => {
      setSubmittingCreds(true);
      try { await submitCredentials(candidates); }
      finally { setSubmittingCreds(false); }
    },
    [submitCredentials],
  );
  const onSkipDevice = useCallback(async () => {
    setSubmittingCreds(true);
    try { await skipDevice(); }
    finally { setSubmittingCreds(false); }
  }, [skipDevice]);

  const onRunAgain = useCallback(async () => {
    clearHash();
    setRunId(null);
    await startRun();
  }, [startRun]);

  const onDone = useCallback(() => {
    clearHash();
    setRunId(null);
    setCollapsed(false);
  }, []);

  // ---- render ------------------------------------------------------

  // No run + not starting: show the entry-point button.
  if (!runId) {
    return (
      <div className="inline-flex w-auto max-w-full items-center gap-3 rounded-md border border-dashed border-border px-3 py-1.5 text-sm">
        <span className="text-muted-foreground">
          {t('equipment.discovery.idleHint')}
        </span>
        <Button size="sm" onClick={startRun} disabled={starting}>
          {starting ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : <Search className="mr-1.5 size-3.5" />}
          {t('equipment.discovery.startMaster')}
        </Button>
      </div>
    );
  }

  // Run exists but is collapsed.
  if (collapsed) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <Search className="size-3.5" />
          {t('equipment.discovery.collapsedHint', { status: t(`equipment.discovery.status.${status}`) })}
        </span>
        <Button size="sm" variant="outline" onClick={() => { setCollapsed(false); void refresh(); }}>
          <ChevronDown className="mr-1.5 size-3.5" />
          {t('equipment.discovery.expand')}
        </Button>
      </div>
    );
  }

  // Run exists, expanded — full panel.
  return (
    <div className="space-y-2 rounded-md border border-border px-3 py-2.5">
      <DiscoveryHeader
        status={status}
        events={events}
        startedAt={run?.started_at}
        onCollapse={() => setCollapsed(true)}
      />

      {isLoading && events.length === 0 ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          {t('common.loading')}
        </div>
      ) : (
        <DiscoveryTimeline events={events} />
      )}

      {/* Credentials prompt when paused */}
      {pendingCredsEvent && isLive(status) && (
        <CredentialPrompt
          deviceLabel={describeDevice(pendingCredsEvent)}
          lastTriedUsername={extractLastUsername(pendingCredsEvent)}
          vendorHint={extractVendorGuess(pendingCredsEvent)}
          submitting={submittingCreds}
          onSubmit={onSubmitCreds}
          onSkip={onSkipDevice}
        />
      )}

      {/* Live controls — cancel + collapse */}
      {isLive(status) && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={() => setCollapsed(true)}>
            <ChevronUp className="mr-1.5 size-3.5" />
            {t('equipment.discovery.collapse')}
          </Button>
          <Button size="sm" variant="destructive" onClick={() => void cancel()}>
            <X className="mr-1.5 size-3.5" />
            {t('equipment.discovery.cancel')}
          </Button>
        </div>
      )}

      {/* Terminal — summary + run-again/done */}
      {isTerminal(status) && (
        <TerminalSummary status={status} run={run} onRunAgain={onRunAgain} onDone={onDone} />
      )}
    </div>
  );
}

// ---- header --------------------------------------------------------

function DiscoveryHeader({
  status,
  events,
  startedAt,
  onCollapse,
}: {
  status: DiscoveryRunStatus;
  events: DiscoveryEvent[];
  startedAt?: string;
  onCollapse: () => void;
}) {
  const { t } = useT();
  const elapsed = formatElapsed(startedAt, events);
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="inline-flex items-center gap-1.5 text-sm font-medium">
        <Search className="size-3.5" />
        <span>{t('equipment.discovery.headerTitle')}</span>
        <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
          colorForStatus(status))}>
          {t(`equipment.discovery.status.${status}`)}
        </span>
        {elapsed && (
          <span className="text-xs font-normal text-muted-foreground tabular-nums">{elapsed}</span>
        )}
      </div>
      <button
        type="button"
        onClick={onCollapse}
        className="rounded p-1 text-muted-foreground hover:text-foreground"
        aria-label={t('equipment.discovery.collapse')}
      >
        <ChevronUp className="size-3.5" />
      </button>
    </div>
  );
}

function colorForStatus(status: DiscoveryRunStatus): string {
  switch (status) {
    case 'ok':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300';
    case 'partial':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300';
    case 'failed':
    case 'cancelled':
      return 'bg-destructive/15 text-destructive';
    case 'paused_on_creds':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300';
    case 'running':
    case 'created':
    default:
      return 'bg-muted text-muted-foreground';
  }
}

// ---- terminal summary ----------------------------------------------

function TerminalSummary({
  status,
  run,
  onRunAgain,
  onDone,
}: {
  status: DiscoveryRunStatus;
  run: ReturnType<typeof useDiscoveryRun>['run'];
  onRunAgain: () => void;
  onDone: () => void;
}) {
  const { t } = useT();
  const counters = (run?.summary?.counters as Record<string, number> | undefined) ?? {};
  const Icon = status === 'failed' ? AlertCircle : status === 'cancelled' ? X : null;
  return (
    <div className="space-y-2 rounded-md bg-muted/40 px-2.5 py-2">
      <div className="text-sm font-medium">
        {Icon && <Icon className="mr-1 inline size-3.5" />}
        {t(`equipment.discovery.summary.terminal.${status}`)}
      </div>
      {Object.keys(counters).length > 0 && (
        <ul className="text-xs text-muted-foreground space-y-0.5">
          {summarizeCounters(counters, t)}
        </ul>
      )}
      <div className="flex items-center justify-between gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={onRunAgain}>
          <RefreshCw className="mr-1.5 size-3.5" />
          {t('equipment.discovery.runAgain')}
        </Button>
        <Button size="sm" onClick={onDone}>
          <Play className="mr-1.5 size-3.5 rotate-90" />
          {t('equipment.discovery.done')}
        </Button>
      </div>
    </div>
  );
}

function summarizeCounters(
  counters: Record<string, number>,
  t: (k: string, p?: Record<string, string | number>) => string,
): React.ReactNode[] {
  const rows: React.ReactNode[] = [];
  for (const kind of ['router', 'nvr', 'camera']) {
    const added = counters[`${kind}_added`] || 0;
    const existing = counters[`${kind}_existing`] || 0;
    const skipped = counters[`${kind}_skipped`] || 0;
    if (added + existing + skipped > 0) {
      rows.push(
        <li key={kind}>
          {t(`equipment.discovery.summary.kind.${kind}`)}:{' '}
          {t('equipment.discovery.summary.line', { added, existing, skipped })}
        </li>,
      );
    }
  }
  if ((counters.errors || 0) > 0) {
    rows.push(<li key="errors">{t('equipment.discovery.summary.errors', { n: counters.errors })}</li>);
  }
  return rows;
}

// ---- helpers -------------------------------------------------------

function readHash(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  if (!hash) return null;
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  return params.get(HASH_KEY);
}

function writeHash(runId: string) {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  params.set(HASH_KEY, runId);
  window.history.replaceState(null, '', `#${params.toString()}`);
}

function clearHash() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  params.delete(HASH_KEY);
  const tail = params.toString();
  window.history.replaceState(null, '', tail ? `#${tail}` : window.location.pathname + window.location.search);
}

/**
 * Find the most recent credential_needed event that hasn't yet been
 * resolved by a subsequent credentials_resolved / credentials_exhausted
 * / device_added event for the same device. Returns undefined when no
 * outstanding prompt.
 *
 * Simple heuristic: walk events from newest backward — if we hit a
 * resolution event first, no outstanding prompt; if we hit a
 * credential_needed first, that's the live one.
 */
function findPendingCredentialNeeded(events: DiscoveryEvent[]): DiscoveryEvent | undefined {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.type === 'credentials_resolved' || e.type === 'device_added' ||
        e.type === 'device_existing' || e.type === 'cancel_requested') {
      return undefined;
    }
    if (e.type === 'credential_needed') {
      return e;
    }
  }
  return undefined;
}

function describeDevice(evt: DiscoveryEvent): string {
  const kind = evt.device_kind ?? 'device';
  const ip = evt.device?.identity?.ip;
  return ip ? `${kind} @ ${ip}` : kind;
}

function extractLastUsername(evt: DiscoveryEvent): string | undefined {
  // Wire shape uses `string | null`; coalesce to undefined at the boundary.
  return evt.device?.hints?.last_tried_username ?? undefined;
}

function extractVendorGuess(evt: DiscoveryEvent): string | undefined {
  const v = evt.device?.hints?.vendor_guess ?? evt.device?.vendor;
  // Vendor hint becomes the placeholder for `username` field — closest
  // mapping is "Hikvision → admin" etc.; for now just return undefined
  // and the prompt defaults to "admin".
  void v;
  return undefined;
}

function formatElapsed(startedAt: string | undefined, events: DiscoveryEvent[]): string {
  if (!startedAt) return '';
  const start = new Date(startedAt).getTime();
  const end = events.length > 0 ? events[events.length - 1].ts_ms : Date.now();
  const ms = Math.max(0, end - start);
  const total = Math.floor(ms / 1000);
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}
